// Adapted from https://github.com/mui/material-ui/tree/b121bd7359e6d0bb01890271d047da159cfb2b08/packages/mui-lab/src/TabPanel
//
// The MIT License (MIT)
//
// Copyright (c) 2014 Call-Em-All
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { unstable_composeClasses as composeClasses } from '@mui/base';
import { InternalStandardProps as StandardProps } from '@mui/material';
import { styled, Theme, useThemeProps } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import clsx from 'clsx';
import * as React from 'react';
import { useLazyMount } from 'src/components/LazyMount/useLazyMount';
import {
  getPanelId,
  getTabId,
  TabContextValue,
  useTabContext,
} from 'src/components/TabContext/TabContext';
import {
  getTabPanelUtilityClass,
  TabPanelClasses,
} from 'src/components/TabPanel/tabPanelClasses';

export { tabPanelClasses } from 'src/components/TabPanel/tabPanelClasses';

//
// The TabPanel component was copied from MUI so that we could tweak its
// behavior to stay mounted if it had ever been activated, as a performance
// optimization when switching between tabs.
//

export interface TabPanelProps
  extends StandardProps<React.HTMLAttributes<HTMLDivElement>> {
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<TabPanelClasses>;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
  /**
   * The `value` of the corresponding `Tab`.
   */
  value: string;
  /**
   * Keep the children in the DOM once the TabPanel
   * has been activated the first time.
   *
   * Set to `false` if you want to unmount the children when a TabPanel has
   * been activated, and then subsequently de-activated.
   * @default true
   */
  keepMounted?: boolean;
  /**
   * An optional custom `React.Context` to use for the TabPanel.
   *
   * This is only necessary in rare cases where multiple `TabContext` providers
   * are nested within each other.
   */
  context?: React.Context<TabContextValue>;
}

const useUtilityClasses = (ownerState: TabPanelProps) => {
  const { classes } = ownerState;

  const slots = {
    root: ['root'],
  };

  return composeClasses(slots, getTabPanelUtilityClass, classes);
};

const TabPanelRoot = styled('div', {
  name: 'MuiTabPanel',
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  padding: theme.spacing(3),
}));

/**
 * TabPanel displays the content associated with the currently selected Tab.
 *
 * Demos:
 *
 * - [Tabs — TabPanel](https://unify.workiva.org/components/tabs?tab=code)
 *
 * API:
 *
 * - [TabPanel API](https://unify.workiva.org/components/tabs?tab=props#tabpanel)
 */
export const TabPanel = React.forwardRef<HTMLDivElement, TabPanelProps>(
  function TabPanel(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiTabPanel' });

    const {
      context: contextProp,
      children,
      className,
      value,
      keepMounted = true,
      ...other
    } = props;

    const ownerState = {
      ...props,
    };

    const classes = useUtilityClasses(ownerState);

    const context = useTabContext(contextProp);
    if (context === null) {
      throw new TypeError(
        'No TabContext provided; make sure your `TabList` and `TabPanel`s are wrapped in a `TabContext`.'
      );
    }
    const id = getPanelId(context, value);
    const tabId = getTabId(context, value);
    const isActivePanel = value === context.value;
    const shouldRenderChildren =
      useLazyMount(isActivePanel) && (keepMounted || isActivePanel);

    return (
      <TabPanelRoot
        aria-labelledby={tabId}
        className={clsx(classes.root, className)}
        hidden={!isActivePanel}
        id={id}
        // @ts-expect-error For some reason `ref` is 'RefObject<unknown>' but only in this assignment
        ref={ref}
        role="tabpanel"
        ownerState={ownerState}
        {...other}
      >
        {shouldRenderChildren && children}
      </TabPanelRoot>
    );
  }
);

export default TabPanel;
